// T-373 — Menu Manager: la superficie principale del "tile Menù" da cui il
// ristoratore gestisce categorie, piatti, prezzi, descrizioni, allergeni,
// diete, foto e on/off — tutto self-serve, online-diretto (RLS-gated).
// QR tavoli / PDF / tema restano nella pagina "avanzate" (/settings/menu).

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonReorder,
  IonReorderGroup,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
  IonToast,
  IonToggle,
  IonToolbar,
  IonTitle,
  useIonViewWillEnter,
} from '@ionic/react';
import {
  addOutline,
  arrowDownOutline,
  arrowUpOutline,
  createOutline,
  eyeOffOutline,
  openOutline,
  settingsOutline,
} from 'ionicons/icons';
import type { ItemReorderEventDetail } from '@ionic/react';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { canEditMenu, type MenuCategory, type MenuItem } from '@trackeat/shared';
import { useAuth } from '../../../lib/auth/AuthProvider.js';
import { useMenu } from './useMenu.js';
import { DishEditorModal } from './DishEditorModal.js';
import { CategoryEditorModal } from './CategoryEditorModal.js';
import { menuTypeFromSearch, menuItemPriceLabel, type MenuType } from './menuType.js';

const PUBLIC_MENU_BASE = 'https://trackeat.eu/menu/';

export function MenuManagerPage(): JSX.Element {
  const { profile } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const view = menuTypeFromSearch(location.search);
  const setView = (type: MenuType): void => history.replace(`${location.pathname}?view=${type}`);
  const menu = useMenu(view);
  const { snapshot } = menu;
  useIonViewWillEnter(menu.reload);

  const canEdit = profile?.is_platform_owner === true || canEditMenu(profile?.role ?? null);
  const online = typeof navigator === 'undefined' ? true : navigator.onLine;

  const [toast, setToast] = useState<string | null>(null);
  const [dishModal, setDishModal] = useState<{ open: boolean; item: MenuItem | null; catId: string | null }>({
    open: false,
    item: null,
    catId: null,
  });
  const [catModal, setCatModal] = useState<{ open: boolean; cat: MenuCategory | null }>({ open: false, cat: null });

  const paperColor = snapshot?.business.menu_theme?.palette?.paper ?? '#ffffff';
  const slug = snapshot?.business.menu_public_slug ?? null;

  const itemsByCat = useMemo(() => {
    const map = new Map<string, MenuItem[]>();
    for (const it of snapshot?.items ?? []) {
      const arr = map.get(it.category_id) ?? [];
      arr.push(it);
      map.set(it.category_id, arr);
    }
    return map;
  }, [snapshot]);

  const categories = snapshot?.categories ?? [];

  async function toggleItemVisible(it: MenuItem): Promise<void> {
    try {
      await menu.editItem(it, { visible: !it.visible });
    } catch (err) {
      setToast((err as Error).message);
    }
  }

  async function onReorderItems(catId: string, ev: CustomEvent<ItemReorderEventDetail>): Promise<void> {
    const arr = (itemsByCat.get(catId) ?? []).slice();
    const moved = ev.detail.complete(arr) as MenuItem[];
    try {
      await menu.reorderItems(moved.map((i) => i.id));
    } catch (err) {
      setToast((err as Error).message);
    }
  }

  async function moveCategory(idx: number, dir: -1 | 1): Promise<void> {
    const arr = categories.slice();
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await menu.reorderCategories(arr.map((c) => c.id));
    } catch (err) {
      setToast((err as Error).message);
    }
  }

  function openPreview(): void {
    if (!slug) {
      setToast('Il locale non ha ancora uno slug pubblico (lo imposta il consulente in Dati locale)');
      return;
    }
    window.open(`${PUBLIC_MENU_BASE}${slug}?view=${view}`, '_blank', 'noopener');
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Menù</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push(`/settings/menu?view=${view}`)} title="QR, PDF, grafica">
              <IonIcon slot="icon-only" icon={settingsOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {!online && (
          <IonNote color="warning" style={{ display: 'block', marginBottom: 12 }}>
            Sei offline: le modifiche al menù richiedono una connessione.
          </IonNote>
        )}
        {!menu.businessId ? (
          <p>Nessun locale attivo.</p>
        ) : menu.isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <IonSpinner />
          </div>
        ) : menu.error ? (
          <IonNote color="danger">Errore nel caricamento: {menu.error.message}</IonNote>
        ) : (
          <>
            {/* Barra publish + anteprima */}
            <IonItem lines="full">
              <IonLabel>
                <strong>Menù pubblico</strong>
                <br />
                <IonNote>{snapshot?.business.menu_published ? 'Visibile ai clienti' : 'Nascosto'}</IonNote>
              </IonLabel>
              <IonToggle
                slot="end"
                disabled={!canEdit}
                checked={snapshot?.business.menu_published ?? false}
                onIonChange={(e) => void menu.setPublished(e.detail.checked).catch((err) => setToast((err as Error).message))}
              />
            </IonItem>
            <div style={{ display: 'flex', gap: 8, margin: '10px 0 4px' }}>
              <IonButton size="small" fill="outline" onClick={openPreview}>
                <IonIcon slot="start" icon={openOutline} />
                Anteprima
              </IonButton>
            </div>

            {!canEdit && (
              <IonNote color="medium" style={{ display: 'block', margin: '8px 0' }}>
                Solo il titolare o il consulente possono modificare il menù. Qui puoi consultarlo.
              </IonNote>
            )}

            {slug === 'lo-sperdicchio' && (
              <IonSegment value={view} onIonChange={(e) => setView(e.detail.value === 'takeaway' ? 'takeaway' : 'menu')} aria-label="Tipo di menù">
                <IonSegmentButton value="menu"><IonLabel>Menù sala</IonLabel></IonSegmentButton>
                <IonSegmentButton value="takeaway"><IonLabel>Menù asporto</IonLabel></IonSegmentButton>
              </IonSegment>
            )}
              {categories.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--ion-color-medium)', padding: '30px 0' }}>
                  <p>Nessuna categoria. {canEdit ? 'Crea la prima per iniziare.' : ''}</p>
                </div>
              )}

              {/* Categorie e piatti */}
              {categories.map((cat, idx) => {
                const its = itemsByCat.get(cat.id) ?? [];
                return (
                  <div key={cat.id} style={{ marginTop: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <h2 style={{ flex: 1, margin: 0, fontSize: 17, opacity: cat.visible ? 1 : 0.5 }}>
                        {cat.icon ? `${cat.icon} ` : ''}
                        {cat.name}
                        {!cat.visible && <IonIcon icon={eyeOffOutline} style={{ marginLeft: 6, fontSize: 14 }} />}
                      </h2>
                      {canEdit && (
                        <>
                          <IonButton size="small" fill="clear" onClick={() => void moveCategory(idx, -1)} disabled={idx === 0}>
                            <IonIcon slot="icon-only" icon={arrowUpOutline} />
                          </IonButton>
                          <IonButton
                            size="small"
                            fill="clear"
                            onClick={() => void moveCategory(idx, 1)}
                            disabled={idx === categories.length - 1}
                          >
                            <IonIcon slot="icon-only" icon={arrowDownOutline} />
                          </IonButton>
                          <IonButton size="small" fill="clear" aria-label={`Modifica categoria ${cat.name}`} onClick={() => setCatModal({ open: true, cat })}>
                            <IonIcon slot="icon-only" icon={createOutline} />
                          </IonButton>
                        </>
                      )}
                    </div>

                    <IonList>
                      <IonReorderGroup disabled={!canEdit} onIonItemReorder={(e) => void onReorderItems(cat.id, e)}>
                        {its.map((it) => (
                          <IonItem key={it.id} button={canEdit} onClick={() => canEdit && setDishModal({ open: true, item: it, catId: cat.id })}>
                            {it.photo_url && (
                              <div
                                slot="start"
                                style={{
                                  width: 46,
                                  height: 46,
                                  borderRadius: 8,
                                  backgroundImage: `url('${it.photo_url}')`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                }}
                              />
                            )}
                            <IonLabel className="ion-text-wrap" style={{ opacity: it.visible ? 1 : 0.5 }}>
                              <strong>{it.name}</strong> {it.out_of_stock && <IonNote color="danger">· esaurito</IonNote>}
                              <br />
                              <IonNote>{menuItemPriceLabel(it)}</IonNote>
                            </IonLabel>
                            {canEdit && (
                              <IonToggle
                                slot="end"
                                checked={it.visible}
                                onClick={(e) => e.stopPropagation()}
                                onIonChange={() => void toggleItemVisible(it)}
                              />
                            )}
                            {canEdit && <IonReorder slot="end" />}
                          </IonItem>
                        ))}
                      </IonReorderGroup>
                      {its.length === 0 && (
                        <IonItem lines="none">
                          <IonNote>Nessun piatto in questa categoria.</IonNote>
                        </IonItem>
                      )}
                      {canEdit && (
                        <IonItem button lines="none" onClick={() => setDishModal({ open: true, item: null, catId: cat.id })}>
                          <IonIcon slot="start" icon={addOutline} color="primary" />
                          <IonLabel color="primary">Aggiungi piatto</IonLabel>
                        </IonItem>
                      )}
                    </IonList>
                  </div>
                );
              })}

              {canEdit && (
                <IonButton
                  expand="block"
                  fill="outline"
                  style={{ marginTop: 20 }}
                  onClick={() => setCatModal({ open: true, cat: null })}
                >
                  <IonIcon slot="start" icon={addOutline} />
                  Aggiungi categoria
                </IonButton>
              )}
          </>
        )}

        {/* Modali */}
        <DishEditorModal
          isOpen={dishModal.open}
          menuType={view}
          item={dishModal.item}
          categories={categories}
          defaultCategoryId={dishModal.catId}
          slug={slug}
          paperColor={paperColor}
          addItem={menu.addItem}
          editItem={menu.editItem}
          removeItem={menu.removeItem}
          onDone={menu.reload}
          onClose={() => setDishModal({ open: false, item: null, catId: null })}
          setToast={setToast}
        />
        <CategoryEditorModal
          isOpen={catModal.open}
          category={catModal.cat}
          itemCount={catModal.cat ? (itemsByCat.get(catModal.cat.id)?.length ?? 0) : 0}
          addCategory={menu.addCategory}
          editCategory={menu.editCategory}
          removeCategory={menu.removeCategory}
          onDone={menu.reload}
          onClose={() => setCatModal({ open: false, cat: null })}
          setToast={setToast}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={3000} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
